import React, { useEffect, useState } from 'react';
import { Bell, Loader2, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import MainLayout from '../components/layout/MainLayout';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { benachrichtigungsZielgruppenApi, kategorienApi } from '../lib/api';

const ROLE_LABELS = {
  WACHLEITUNG: 'Wachleitung',
  WACHLEITUNG_VERTRETUNG: 'Wachleitung Vertretung',
  WACHABTEILUNGSLEITUNG: 'Wachabteilungsleitung',
  ADMIN: 'Admin',
  MITARBEITER: 'Mitarbeiter',
};
const EMPTY = {
  kategorien_ids: [],
  rollen: [],
  user_ids: [],
  abteilung_ids: [],
  permission_keys: [],
  excluded_user_ids: [],
  match_mode: 'ANY',
  ist_aktiv: true,
};

function ChoiceGrid({ title, choices, values, onToggle, emptyText }) {
  return (
    <div>
      <p className="text-sm font-medium mb-2">{title}</p>
      {choices.length === 0 ? (
        <p className="text-xs text-muted-foreground">{emptyText || 'Keine Einträge vorhanden'}</p>
      ) : (
        <div className="grid gap-1 sm:grid-cols-2">
          {choices.map((choice) => (
            <label key={choice.key} className="flex items-center gap-2 rounded border p-2 cursor-pointer">
              <input type="checkbox" checked={values.includes(choice.key)} onChange={() => onToggle(choice.key)} />
              <span className="text-sm">{choice.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export default function BenachrichtigungsZielgruppenPage() {
  const [meta, setMeta] = useState(null);
  const [groups, setGroups] = useState([]);
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [targetResult, groupsResult] = await Promise.all([
        benachrichtigungsZielgruppenApi.getAll(),
        kategorienApi.list(),
      ]);
      setMeta(targetResult.data);
      setGroups(groupsResult.data || []);
      const initial = {};
      Object.entries(targetResult.data.configs || {}).forEach(([key, config]) => {
        initial[key] = { ...EMPTY, ...config };
      });
      setDraft(initial);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Empfängerregeln konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = (type, field, value) => {
    setDraft((current) => {
      const config = current[type] || { ...EMPTY };
      const values = config[field] || [];
      return {
        ...current,
        [type]: {
          ...config,
          [field]: values.includes(value)
            ? values.filter((item) => item !== value)
            : [...values, value],
        },
      };
    });
  };

  const save = async (type) => {
    setSaving(type);
    try {
      await benachrichtigungsZielgruppenApi.upsert(type, draft[type] || EMPTY);
      toast.success('Empfängerregel gespeichert');
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(null);
    }
  };

  const remove = async (type) => {
    setSaving(type);
    try {
      await benachrichtigungsZielgruppenApi.remove(type);
      toast.success('Regel deaktiviert – dieser Typ wird nicht allgemein verteilt');
      await load();
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return <MainLayout><Loader2 className="w-6 h-6 mx-auto mt-16 animate-spin" /></MainLayout>;
  }

  const roleChoices = (meta?.rollen || []).map((key) => ({ key, label: ROLE_LABELS[key] || key }));
  const groupChoices = groups.map((group) => ({ key: group.id, label: group.name }));
  const userChoices = (meta?.users || []).map((user) => ({ key: user.id, label: `${user.nachname}, ${user.vorname}` }));
  const departmentChoices = (meta?.abteilungen || []).map((department) => ({ key: department.id, label: department.name }));
  const permissionChoices = meta?.permissions || [];

  return (
    <MainLayout>
      <div className="max-w-6xl mx-auto p-4 space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Bell className="w-5 h-5" /> Benachrichtigungs-Zielgruppen</CardTitle>
            <CardDescription>
              Für jeden Meldungstyp wird individuell festgelegt, wer ihn erhält. Direkte persönliche Meldungen – etwa ein abgelehnter Dienstwunsch – werden von diesen Filtern nicht blockiert.
            </CardDescription>
          </CardHeader>
        </Card>

        {(meta?.typen || []).map((type) => {
          const active = !!draft[type.key];
          const config = draft[type.key] || { ...EMPTY };
          return (
            <Card key={type.key}>
              <CardHeader className="flex flex-row items-start justify-between gap-3">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    {type.label}
                    <Badge variant={active ? 'secondary' : 'outline'}>{active ? 'konfiguriert' : 'nicht konfiguriert'}</Badge>
                  </CardTitle>
                  <CardDescription>{active ? `Verknüpfung: ${config.match_mode}` : 'Ohne Regel keine allgemeine Verteilung'}</CardDescription>
                </div>
                <div className="flex gap-2">
                  {active && <Button variant="ghost" size="sm" onClick={() => remove(type.key)} disabled={saving === type.key}><Trash2 className="w-4 h-4 mr-1" /> Deaktivieren</Button>}
                  <Button size="sm" onClick={() => active ? save(type.key) : setDraft((current) => ({ ...current, [type.key]: { ...EMPTY } }))} disabled={saving === type.key}>
                    {saving === type.key ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                    {active ? 'Speichern' : 'Konfigurieren'}
                  </Button>
                </div>
              </CardHeader>
              {active && (
                <CardContent className="space-y-5">
                  <div className="rounded border p-3">
                    <p className="text-sm font-medium mb-2">Match-Modus</p>
                    <div className="flex gap-4">
                      {['ANY', 'ALL'].map((mode) => (
                        <label key={mode} className="flex items-center gap-2">
                          <input type="radio" name={`mode-${type.key}`} checked={config.match_mode === mode} onChange={() => setDraft((current) => ({ ...current, [type.key]: { ...config, match_mode: mode } }))} />
                          <span className="text-sm">{mode === 'ANY' ? 'ANY – mindestens eine Bedingung' : 'ALL – alle gesetzten Bedingungen'}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="grid gap-5 lg:grid-cols-2">
                    <ChoiceGrid title="Hauptrollen" choices={roleChoices} values={config.rollen} onToggle={(value) => toggle(type.key, 'rollen', value)} />
                    <ChoiceGrid title="Benutzergruppen" choices={groupChoices} values={config.kategorien_ids} onToggle={(value) => toggle(type.key, 'kategorien_ids', value)} />
                    <ChoiceGrid title="Einzelne Benutzer" choices={userChoices} values={config.user_ids} onToggle={(value) => toggle(type.key, 'user_ids', value)} />
                    <ChoiceGrid title="Wachabteilungen" choices={departmentChoices} values={config.abteilung_ids} onToggle={(value) => toggle(type.key, 'abteilung_ids', value)} />
                    <ChoiceGrid title="Effektive Berechtigungen (optional)" choices={permissionChoices} values={config.permission_keys} onToggle={(value) => toggle(type.key, 'permission_keys', value)} />
                    <ChoiceGrid title="Benutzer ausschließen" choices={userChoices} values={config.excluded_user_ids} onToggle={(value) => toggle(type.key, 'excluded_user_ids', value)} />
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>
    </MainLayout>
  );
}
